import React from 'react'
import { useTheme } from '@emotion/react'

import { CommonCardProps } from 'component-library/cards/CardItem.Props'
import {
    StyledHeroCard,
    StyledLink,
    CardTextContainer,
    StyledHeroCardMedia,
    StyledHeroCardText,
    StyledCell,
} from 'component-library/cards/HeroCard/HeroCard.styled'
import { CardByline } from 'component-library/cards/CardByline/CardByline'

import { toLinkWithHint } from 'common/routing/route-hints'
import { SponsoredLabel } from 'component-library/advertising/SponsoredLabel/SponsoredLabel'
import { KickerPosition } from '../Kicker/Kicker.styled'
import { themedValue } from 'component-library/__styling/themed-value'
import { FontScales } from 'component-library/__styling/settings/fontScale'
import { MaybeLoaded } from 'json-react-layouts-data-loader'
import { FixedRatio } from 'common/client-data-types/image-item'
import { isVideoClick } from 'component-library/__helpers/video-card-helpers'
import { GetVideoQueue } from 'component-library/content/Video/PlayerInterface'

import {
    getVideoQueue,
    VideoQueue,
} from 'component-library/templates/Publication/lib/get-video-queue'
import { ComponentServices } from 'common/routing/page-definition'
import { PublicationCardItem } from 'common/client-data-types/card-types'
import { createCardClickedEvent } from 'common/events/collection-events'

export type HeroVideoLayoutRatios = '1:1' | '3:1' | '1:3'

export interface Props extends CommonCardProps {
    hasBackground?: boolean
    imageLayout?: 'fill-container-horizontally' | 'none'
    kickerPosition?: KickerPosition
    kickerMode?: 'hidden' | 'large' | 'default'
    /**
     * defaults to `4:3`
     */
    fixedRatio?: FixedRatio
    cardNumber: number
    item: MaybeLoaded<PublicationCardItem>
    getVideoQueue?: GetVideoQueue
    cardServices?: ComponentServices
    layoutRatio?: HeroVideoLayoutRatios
}

export const HeroVideoCard: React.FC<Props> = props => {
    const linkClicked = (e: React.MouseEvent<HTMLElement>) => {
        if (!props.item.loaded) {
            // We don't want the link to do anything
            e.preventDefault()
            return
        }

        if (
            e.nativeEvent.target instanceof HTMLElement &&
            isVideoClick(e.nativeEvent.target.className)
        ) {
            e.preventDefault()
            return
        } else {
            props.onEvent(
                createCardClickedEvent(
                    props.item.result,
                    'InternalHeroCard',
                    'HeroCard',
                    props.cardNumber
                )
            )
        }
    }

    const returnVideoQueue = () => {
        if (!props.item.loaded) {
            return Promise.resolve<VideoQueue>({
                type: 'not-playlist-video',
                items: [],
            })
        }

        if (props.item.loaded && props.item.result.id && props.cardServices) {
            getVideoQueue(props.cardServices, props.item.result.id)
        }

        return Promise.resolve<VideoQueue>({
            type: 'not-playlist-video',
            items: [],
        })
    }

    const theme = useTheme()
    const {
        className,
        hasBackground,
        imageLayout,
        hideKicker,
        hideByline,
        kickerPosition,
        hideTeaser,
        kickerMode,
        onEvent,
        item,
        fixedRatio,
        layoutRatio = '1:1',
    } = props

    const showByline = theme.kind !== 'perthnow' && !hideByline
    const showSponsoredLabel = item.loaded && item.result.isSponsored
    const primaryTopic = item.loaded ? item.result.primaryTopic.id : undefined

    const fontScale = themedValue(theme, {
        thewest: 1.75,
        sevennews: 1.7,
        fallback: 2,
    }) as FontScales

    return (
        <StyledHeroCard
            className={className}
            data-topic={primaryTopic}
            isSponsored={showSponsoredLabel}
            hasBackground={hasBackground}
            primaryTopic={primaryTopic}
        >
            <StyledLink
                to={toLinkWithHint(item)}
                onClick={linkClicked}
                hasBackground={hasBackground}
                isLoading={!item.loaded}
            >
                <StyledCell layoutRatio={layoutRatio}>
                    <CardTextContainer>
                        <StyledHeroCardText
                            item={item}
                            hasBackground={hasBackground}
                            hideKicker={hideKicker}
                            hideTeaser={hideTeaser}
                            largeKicker={kickerMode === 'large'}
                            fontScale={fontScale}
                            onEvent={onEvent}
                            kickerPosition={kickerPosition}
                        />
                        {showByline && (
                            <CardByline item={item} onEvent={onEvent} showProfile={true} />
                        )}
                        {showSponsoredLabel && <SponsoredLabel hasBackground={hasBackground} />}
                    </CardTextContainer>
                </StyledCell>
                <StyledCell layoutRatio={layoutRatio}>
                    <StyledHeroCardMedia
                        isLarge
                        item={item}
                        fixedRatio={fixedRatio}
                        hasBackground={hasBackground}
                        imageWidths={500}
                        onEvent={onEvent}
                        className={className}
                        mediaMode="heroVideoInline"
                        // Hero card should never be lazy loaded
                        disableImageLazyLoad={true}
                        willPlayVideoInline={true}
                        getVideoQueue={returnVideoQueue}
                        imageLayout={imageLayout || 'none'}
                    />
                </StyledCell>
            </StyledLink>
        </StyledHeroCard>
    )
}
HeroVideoCard.displayName = 'HeroVideoCard'
